import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener, Inject,
  Input,
  OnInit,
  Output, PLATFORM_ID,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { EventItem, EventItemType } from "@model";
import { NgbDate, NgbDateParserFormatter, NgbOffcanvas, NgbOffcanvasRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {
  getDateFromNgbDate,
  getNgbDate
} from '@utility';
import {isPlatformBrowser} from "@angular/common";

@Component({
  selector: 'app-date-range-selector',
  templateUrl: './date-range-selector.component.html',
  styleUrls: ['./date-range-selector.component.scss']
})
export class DateRangeSelectorComponent implements OnInit {

  @Input() largeInput: boolean = false;
  @Input() fromDate: NgbDate | any = null;
  @Input() toDate: NgbDate | any = null;
  @Input() maxDate: NgbDate | undefined;
  @Input() minDate: NgbDate | undefined;
  @Input() wrapperWidth?: string;
  @Input() placeholder: string = '';

  calendarType: EventItemType = EventItemType.UPCOMING;
  EventItemType = EventItemType;
  calendarEnd: NgbDate = getNgbDate(30);
  calendarStart: NgbDate = getNgbDate(-30);
  @Output() dateChanged = new EventEmitter<{ from?: NgbDate | undefined, to?: NgbDate | undefined }>();

  isBrowser: boolean = false;
  navigationDate: Date = new Date()

  opened: boolean = false;
  pendingMonthRange: { from?: NgbDate, to?: NgbDate } = {};
  @ViewChild('dropdownRef', { static: false }) dropdown: any;
  @ViewChild('offcanvasRef') offcanvasTemplate?: TemplateRef<any>;

  constructor(public formatter: NgbDateParserFormatter, private translocoService: TranslocoService, @Inject(PLATFORM_ID) private platformId: Object,
              private offcanvasService: NgbOffcanvas
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (this.maxDate) {
      this.calendarEnd = this.maxDate;
    }
    if (this.minDate) {
      this.calendarStart = this.minDate;
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      this.onCalendarClose();
    }
  }

  onOpenDropdown() {
    this.onCalendarClose();
    this.setNavigationDate();
    this.pendingMonthRange = { from: this.fromDate, to: this.toDate };
    this.dropdown.open();
  }

  setNavigationDate() {
    const date =
        this.fromDate ? getDateFromNgbDate(this.fromDate) :
            this.toDate ? getDateFromNgbDate(this.toDate) :
                undefined;

    this.navigationDate = date ?? new Date();
  }

  onOpenOffCanvas() {
    if (this.isBrowser) {
      this.setNavigationDate();
      this.onCalendarClose();
      const offcanvasRef: NgbOffcanvasRef = this.offcanvasService.open(
          this.offcanvasTemplate,
          {
            position: 'bottom',
            scroll: false,
            panelClass: 'date-canvas',
            backdropClass: 'date-backdrop',
          }
      );
      offcanvasRef.result.finally(() => (
          this.dateChanged.emit({from: this.fromDate, to: this.toDate})
      ));
    }
  }

  onCalendarClose(): void {
      this.offcanvasService.dismiss();
      this.dropdown.close();
  }

  onApplyClick() {
    if (!this.pendingMonthRange?.from || !this.pendingMonthRange?.to) {
      this.onCalendarClose();
      return;
    }

    this.fromDate = this.pendingMonthRange.from;
    this.toDate = this.pendingMonthRange.to;

    this.dateChanged.emit({ from: this.fromDate, to: this.toDate });
    this.onCalendarClose();
  }

  onRangeChange(data: {from?: NgbDate | undefined, to?: NgbDate | undefined}) {
      this.fromDate = data.from;
      this.toDate = data.to;
  }

  getValue(): string {
    let value = '';
    if (this.fromDate && this.toDate) {
      const fromText = this.translocoService.translate('months.picker.short.' + this.formatMonth(this.fromDate)) + ' ' + this.formatYear(this.fromDate);
      value = fromText;
      const toText = this.translocoService.translate('months.picker.short.' + this.formatMonth(this.toDate)) + ' ' + this.formatYear(this.toDate);
      value = (fromText === toText) ? fromText : `${fromText} – ${toText}`;

      return value;
    }
    return '';
  }

  dropdownClosed(closed: boolean) {

  }

  private formatMonth(d: NgbDate): string {
    const jsDate = new Date(d.year, d.month - 1, 1);
    return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(jsDate).toLowerCase();
  }

  private formatYear(d: NgbDate): string {
    return String(d.year);
  }
}
